import { createFileRoute, Link, useNavigate, useSearch, redirect } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { verifiedSignIn } from "@/lib/auth.functions";
import { TwoFactorChallenge, needsMfaChallenge } from "@/components/two-factor-challenge";
import { Eye, EyeOff } from "lucide-react";

function sanitizeRedirect(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return "/";
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return "/";
  }
}

export async function resolvePostLoginDestination(defaultRedirect: string, userId: string) {
  defaultRedirect = sanitizeRedirect(defaultRedirect);
  const { data: rolesRows } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  const roles = (rolesRows ?? []).map((r) => r.role as string);

  // Admins must use the dedicated admin sign-in page. Signal to caller.
  if (roles.includes("admin")) return "__ADMIN_BLOCKED__";

  if (defaultRedirect && defaultRedirect !== "/") return defaultRedirect;
  if (roles.includes("creator")) return "/creator-dashboard";
  return "/"; // shoppers land on the home/discover feed
}

export const Route = createFileRoute("/login")({
  validateSearch: (s: Record<string, unknown>) => ({ redirect: (s.redirect as string) || "/" }),
  beforeLoad: async ({ search }) => {
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal && aal.currentLevel === "aal1" && aal.nextLevel === "aal2") {
        // Session exists but MFA hasn't been completed — let the login page render
        // its own challenge UI instead of redirecting away.
        return;
      }
      const destination = await resolvePostLoginDestination(sanitizeRedirect(search.redirect), data.session.user.id);
      if (destination === "__ADMIN_BLOCKED__") {
        await supabase.auth.signOut();
        throw redirect({ to: "/admin-login" });
      }
      throw redirect({ to: destination });
    }
  },
  head: () => ({
    meta: [
      { title: "Sign in — Pluggz" },
      { name: "description", content: "Sign in to your Pluggz account to manage your creator storefront, track commissions, or access your saved picks and wishlist." },
      { property: "og:title", content: "Sign in to Pluggz" },
      { property: "og:description", content: "Access your creator dashboard, commissions, and saved picks." },
      { property: "og:url", content: `${SITE_URL}/login` },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/login` }],
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/login" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [magicMode, setMagicMode] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);

  async function finishSignIn(userId: string) {
    const destination = await resolvePostLoginDestination(sanitizeRedirect(search.redirect), userId);
    if (destination === "__ADMIN_BLOCKED__") {
      await supabase.auth.signOut();
      setMfaRequired(false);
      return setError("Admin accounts must sign in from the Admin sign-in page.");
    }
    navigate({ to: destination });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const recaptchaToken = await getRecaptchaToken("login");
    const result = await verifiedSignIn({ data: { email, password, recaptchaToken } });
    if (!result.ok) {
      setLoading(false);
      return setError(result.error);
    }
    const { error: setErr } = await supabase.auth.setSession(result.session);
    setLoading(false);
    if (setErr) return setError(setErr.message);
    if (await needsMfaChallenge()) {
      setPendingUserId(result.userId);
      setMfaRequired(true);
      return;
    }
    await finishSignIn(result.userId);
  }

  async function oauth(provider: "google" | "apple") {
    setError(null);
    const result = await lovable.auth.signInWithOAuth(provider, { redirect_uri: window.location.origin });
    if (result.error) return setError(result.error.message ?? "Sign-in failed");
    if (result.redirected) return;

    const { data } = await supabase.auth.getUser();
    if (!data.user) return navigate({ to: sanitizeRedirect(search.redirect) });
    if (await needsMfaChallenge()) {
      setPendingUserId(data.user.id);
      setMfaRequired(true);
      return;
    }
    await finishSignIn(data.user.id);
  }

  async function sendMagicLink(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error: otpErr } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    setLoading(false);
    if (otpErr) return setError(otpErr.message);
    setMagicSent(true);
  }

  return (
    <div className="relative mx-auto w-full max-w-md overflow-hidden px-4 py-12 sm:px-6 sm:py-20">
      <div aria-hidden className="pointer-events-none absolute -left-20 top-10 h-48 w-48 rounded-full bg-accent/20 blur-3xl float-slow sm:h-64 sm:w-64" />
      <div aria-hidden className="pointer-events-none absolute -right-20 bottom-20 h-48 w-48 rounded-full bg-sand/30 blur-3xl float-slow sm:h-64 sm:w-64" style={{ animationDelay: "-3s" }} />
      <div className="relative">
        <p className="editorial-eyebrow">Welcome back</p>
        <h1 className="mt-2 font-serif text-3xl sm:text-4xl">
          Sign <span className="italic shimmer">in</span>
        </h1>

        <button
          type="button"
          onClick={() => oauth("google")}
          disabled={loading}
          className="mt-8 flex w-full items-center justify-center gap-2 border border-border bg-card/80 px-6 py-3 text-sm uppercase tracking-widest text-foreground backdrop-blur transition-all duration-300 hover:scale-[1.01] disabled:opacity-50"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continue with Google
        </button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase tracking-widest">
            <span className="bg-background px-3 text-muted-foreground">or</span>
          </div>
        </div>

        {!magicMode && (
          <button
            type="button"
            onClick={() => { setMagicMode(true); setMagicSent(false); setError(null); }}
            disabled={loading}
            className="mb-6 flex w-full items-center justify-center gap-2 border border-border bg-card/80 px-6 py-3 text-sm uppercase tracking-widest text-foreground backdrop-blur transition-all duration-300 hover:scale-[1.01] disabled:opacity-50"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            Sign in with magic link
          </button>
        )}

        {magicSent ? (
          <div className="space-y-4">
            <p className="text-center text-sm text-muted-foreground">Check your inbox for a sign-in link.</p>
            <button
              type="button"
              onClick={() => { setMagicMode(false); setMagicSent(false); setError(null); }}
              className="w-full text-center text-xs text-muted-foreground hover:text-foreground link-underline"
            >
              Use password instead
            </button>
          </div>
        ) : mfaRequired && pendingUserId ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Two-factor authentication is required for this account.
            </p>
            <TwoFactorChallenge
              onVerified={() => finishSignIn(pendingUserId)}
              onCancel={async () => {
                await supabase.auth.signOut();
                setMfaRequired(false);
                setPendingUserId(null);
              }}
            />
          </div>
        ) : magicMode ? (
          <form onSubmit={sendMagicLink} className="space-y-3">
            <div>
              <label htmlFor="login-email" className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">Email</label>
              <input id="login-email" required type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full border border-border bg-card/80 backdrop-blur px-4 py-3 text-base outline-none transition-colors focus:border-foreground sm:text-sm" />
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
            <button disabled={loading} type="submit" className="w-full bg-foreground px-6 py-3 text-sm uppercase tracking-widest text-background transition-all duration-300 hover:scale-[1.01] disabled:opacity-50">
              {loading ? "Sending…" : "Send magic link"}
            </button>
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => { setMagicMode(false); setError(null); }}
                className="text-xs text-muted-foreground hover:text-foreground link-underline"
              >
                Use password instead
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label htmlFor="login-email" className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">Email</label>
              <input id="login-email" required type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full border border-border bg-card/80 backdrop-blur px-4 py-3 text-base outline-none transition-colors focus:border-foreground sm:text-sm" />
            </div>
            <div>
              <label htmlFor="login-password" className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">Password</label>
              <div className="relative">
                <input id="login-password" required type={showPassword ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" className="w-full border border-border bg-card/80 backdrop-blur px-4 py-3 pr-10 text-base outline-none transition-colors focus:border-foreground sm:text-sm" />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground link-underline">Forgot password?</Link>
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
            <button disabled={loading} type="submit" className="w-full bg-foreground px-6 py-3 text-sm uppercase tracking-widest text-background transition-all duration-300 hover:scale-[1.01] disabled:opacity-50">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}

        <p className="mt-8 text-center text-sm text-muted-foreground">
          New here? <Link to="/signup" className="link-underline text-foreground">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
